/**
 * PaymentPlanComparisonTable — os planos do paywall lado a lado, numa leitura
 * só.
 *
 * POR QUE: o paywall empilha um cartão por plano. Para comparar mensal,
 * semestral e anual o usuário precisa abrir um, decorar preço e ciclo, rolar,
 * abrir o outro e refazer a conta de cabeça — o beco que a vitrine registrou em
 * #244 ("comparar exige abrir um, decorar e voltar").
 *
 * O QUE ELA **NÃO** FAZ: não vende. Não há botão, link, `checkoutUrl` nem
 * `window.open` aqui. A compra continua exclusivamente nos cartões, com o mesmo
 * preço e o mesmo CTA de antes — esta tabela é superfície de DECISÃO, não de
 * oferta. Cada célula sai de `payment-plan-facts`, o mesmo módulo que alimenta
 * os cartões, então as duas leituras não podem divergir.
 *
 * Exemplo:
 *   <PaymentPlanComparisonTable plans={visiblePlans} />
 */
import { formatSaleMoneyCents } from "@/lib/format/currency";
import type { PublicPlanView } from "../types";
import {
  formatMonthlyEquivalent,
  formatPlanHeadline,
  formatPlanTerm,
} from "./payment-plan-facts";

/** Comparar UM plano não é comparar: abaixo de dois a tabela não aparece. */
const MIN_PLANS_TO_COMPARE = 2;

type ComparisonRow = {
  readonly label: string;
  readonly values: readonly string[];
};

export function PaymentPlanComparisonTable(props: {
  readonly plans: readonly PublicPlanView[];
}): React.ReactElement | null {
  if (props.plans.length < MIN_PLANS_TO_COMPARE) return null;
  const rows = comparisonRows(props.plans);
  return (
    <section
      data-testid="payment-plan-comparison"
      aria-label="Comparativo dos planos"
      className="overflow-x-auto rounded-[24px] border border-border bg-surface p-3"
    >
      <table className="w-full min-w-[320px] border-collapse text-left">
        <ComparisonHead plans={props.plans} />
        <tbody>
          {rows.map((row) => (
            <ComparisonRowCells key={row.label} row={row} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ComparisonHead(props: {
  readonly plans: readonly PublicPlanView[];
}): React.ReactElement {
  return (
    <thead>
      <tr>
        <th scope="col" className="w-[86px] pb-2 pr-2">
          <span className="sr-only">Característica</span>
        </th>
        {props.plans.map((plan) => (
          <th
            key={plan.id}
            scope="col"
            data-testid={`payment-comparison-head-${plan.id}`}
            className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground"
          >
            {plan.displayName}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function ComparisonRowCells(props: {
  readonly row: ComparisonRow;
}): React.ReactElement {
  return (
    <tr className="border-t border-border align-top">
      <th
        scope="row"
        className="py-2 pr-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/62"
      >
        {props.row.label}
      </th>
      {props.row.values.map((value, index) => (
        <td
          // A célula é identificada pela coluna, e duas colunas podem repetir o
          // mesmo texto ("30 dias"): a chave precisa do índice.
          key={`${props.row.label}-${String(index)}`}
          className="px-2 py-2 text-[11px] leading-4 text-foreground"
        >
          {value}
        </td>
      ))}
    </tr>
  );
}

/**
 * Uma linha só entra quando TODOS os planos visíveis a respondem. Célula vazia
 * num comparativo lê como "este plano não tem" — foi assim que o "IA —" chegou
 * à produção (token-offer-catalog.ts, removido em 05/08). Melhor uma linha a
 * menos do que um traço que mente.
 */
function comparisonRows(
  plans: readonly PublicPlanView[],
): readonly ComparisonRow[] {
  const rows: ComparisonRow[] = [
    { label: "Preço", values: plans.map(planPriceCell) },
    { label: "Ciclo", values: plans.map(formatPlanTerm) },
    { label: "Inclui", values: plans.map(formatPlanHeadline) },
  ];
  const rateio = plans.map(formatMonthlyEquivalent);
  if (rateio.every((value): value is string => value !== null)) {
    rows.push({ label: "Rateio", values: rateio });
  }
  return rows;
}

function planPriceCell(plan: PublicPlanView): string {
  return formatSaleMoneyCents(plan.amountCents);
}
